import React, { useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Radar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "../assets/css/homepage_style.css";
import "../assets/css/reimagine_light.css";
import "../assets/css/roadmap.css";
import "../assets/css/report-theme.css";
import Navbar from "../components/Navigation";
import Footer from "../components/Footer";
import ScrollToExplore from "../components/ScrollToExplore";

// Register Chart.js components
ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
);

function Roadmap() {
  const basename = process.env.PUBLIC_URL || "";
  const location = useLocation();
  const navigate = useNavigate();
  const [openCard, setOpenCard] = useState(null);

  // Redirect to ReImagineLite if no data is present
  useEffect(() => {
    const reportData = location.state?.reportData;
    const cardAnswers = location.state?.cardAnswers;
    
    // Check if essential data is missing
    if (!reportData || !cardAnswers) {
      navigate('/reimagine-lite', { replace: true });
    }
  }, [location.state, navigate]);

  const handleGenerateNewReport = () => {
    // Navigate back to ReImagineLite page to generate a new report
    // Using replace: true to prevent users from going back to this roadmap page
    // This ensures they must generate a new report before seeing a roadmap again
    navigate('/reimagine-lite', { replace: true });
    
    // Scroll to top of the page
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleProceedToSignin = () => {
    // Open Outlook with a draft email pre-filled with subject and body
    const emailAddress = "info@reimagine.traxccel.com";

    const subject =
      "Your ReIMAGINE Lite Roadmap Is Ready – Next Step to Begin Your Full Project";

    const body = [
      "Dear User,",
      "",
      "Thank you for trying out ReIMAGINE Lite and generating your initial roadmap. We hope the insights provided gave you a helpful starting point for understanding your Data + AI priorities.",
      "",
      "To move forward, you can now request the creation of a full project inside ReIMAGINE 2.0. This will enable you to explore the complete capability of our Data + AI strategy platform, including detailed assessments, opportunity mapping, value modeling, and roadmap refinement with our enhanced features.",
      "",
      "If you would like us to set up your project in ReIMAGINE 2.0, please reply to this email with a confirmation. Once we receive your request, our team will:",
      "",
      "Create your dedicated project space in ReIMAGINE 2.0",
      "",
      "Share your login credentials",
      "",
      "Provide step-by-step guidelines on how to use the platform effectively",
      "",
      "We look forward to supporting you as you advance your Data + AI strategy using ReIMAGINE 2.0.",
      "",
      "Best regards,",
      "",
      "Team Traxccel",
    ].join("\n");

    const mailtoLink = `mailto:${emailAddress}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    window.location.href = mailtoLink;
  };

  const defaultCardAnswers = {
    "project-type": "",
    maturity: "",
    sector: "",
    function: "",
    capability: "",
    impact: "",
    dummy1: "",
    dummy2: "",
    dummy3: "",
  };

  const cardAnswers = {
    ...defaultCardAnswers,
    ...(location.state?.cardAnswers || {}),
  };

  // Get report data from location state
  const reportData = location.state?.reportData;
  const assessmentReport = reportData?.data?.assessment_report || {};
  const overallRecommendation = reportData?.data?.overall_recommendation || {};
  const summary = overallRecommendation?.summary || "";
  const actionPlan = overallRecommendation?.action_plan || [];
  const scores = reportData?.data?.score || {};

  // Calculate counts for Strong, Moderate, and Needs Focus areas
  const calculateScoreCounts = () => {
    const scoreValues = Object.values(scores);
    const strongCount = scoreValues.filter((score) => score === 100).length;
    const moderateCount = scoreValues.filter((score) => score === 50).length;
    const needsFocusCount = scoreValues.filter((score) => score === 0).length;
    return { strongCount, moderateCount, needsFocusCount };
  };

  const { strongCount, moderateCount, needsFocusCount } =
    calculateScoreCounts();

  // Prepare spider chart data from scores
  const spiderChartData = useMemo(() => {
    if (!scores || Object.keys(scores).length === 0) {
      return null;
    }

    const labels = Object.keys(scores);
    const dataValues = Object.values(scores);

    // Use labels as is - formatting will be done in pointLabels callback
    const formattedLabels = labels;

    // Create gradient colors based on score values
    const getPointColor = (score) => {
      if (score === 100) return "rgba(74, 222, 128, 1)"; // Green for 100
      if (score === 50) return "rgba(251, 191, 36, 1)"; // Yellow for 50
      return "rgba(239, 68, 68, 1)"; // Red for 0
    };

    return {
      labels: formattedLabels,
      datasets: [
        {
          label: "AI Readiness Score",
          data: dataValues,
          backgroundColor: "rgba(23, 176, 253, 0.22)",
          borderColor: "#0f77d5",
          borderWidth: 1.5,
          pointBackgroundColor: dataValues.map((score) => getPointColor(score)),
          pointBorderColor: "#ffffff",
          pointBorderWidth: 2,
          pointHoverBackgroundColor: dataValues.map((score) =>
            getPointColor(score),
          ),
          pointHoverBorderColor: "#ffffff",
          pointHoverBorderWidth: 3,
          pointRadius: 6,
          pointHoverRadius: 8,
          tension: 0.1,
        },
      ],
    };
  }, [scores]);

  const spiderChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 1,
    layout: {
      padding: {
        top: 10,
        right: 10,
        bottom: 10,
        left: 10,
      },
    },
    animation: {
      duration: 2000,
      easing: "easeOutQuart",
      animateRotate: true,
      animateScale: true,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: "#ffffff",
        titleColor: "#1a2430",
        bodyColor: "#1a2430",
        padding: 12,
        titleFont: {
          size: 14,
          weight: "bold",
        },
        bodyFont: {
          size: 13,
        },
        borderColor: "rgba(15, 119, 213, 0.35)",
        borderWidth: 2,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          title: function (context) {
            if (!context || context.length === 0 || !context[0]) {
              return "";
            }
            return context[0].label;
          },
          label: function (tooltipItem) {
            if (!tooltipItem || !tooltipItem.parsed) {
              return "";
            }
            const score = tooltipItem.parsed.r;
            let status = "";
            if (score === 100) status = " (Strong)";
            else if (score === 50) status = " (Moderate)";
            else status = " (Needs Focus)";

            // For zero-score items, return empty string if we have multiple zero-score items
            // (they'll be shown in afterBody instead to avoid duplicates)
            if (score === 0 && tooltipItem.chart && tooltipItem.chart.data) {
              const chartData = tooltipItem.chart.data;
              const labels = chartData.labels || [];
              const dataValues =
                chartData.datasets && chartData.datasets[0]
                  ? chartData.datasets[0].data || []
                  : [];
              let zeroCount = 0;
              labels.forEach((label, index) => {
                if (dataValues[index] === 0) {
                  zeroCount++;
                }
              });
              // If multiple zero-score items, skip individual label to prevent duplicates
              // They'll all be shown in afterBody
              if (zeroCount > 1) {
                return "";
              }
            }

            return `Score: ${score}%${status}`;
          },
          afterBody: function (context) {
            // Check if hovering on a red dot (score 0)
            if (
              !context ||
              context.length === 0 ||
              !context[0] ||
              !context[0].parsed
            ) {
              return "";
            }

            const hoveredScore = context[0].parsed.r;
            // Only show all zero-score categories if hovering on a zero-score point
            if (
              hoveredScore === 0 &&
              context[0].chart &&
              context[0].chart.data
            ) {
              const chartData = context[0].chart.data;
              const labels = chartData.labels || [];
              const dataValues =
                chartData.datasets && chartData.datasets[0]
                  ? chartData.datasets[0].data || []
                  : [];
              const zeroScoreItems = [];

              // Find all categories with 0% score
              labels.forEach((label, index) => {
                if (dataValues[index] === 0) {
                  zeroScoreItems.push(`• ${label}: Score: 0% (Needs Focus)`);
                }
              });

              // Show all zero-score categories
              if (zeroScoreItems.length > 0) {
                return ["", ...zeroScoreItems];
              }
            }
            return "";
          },
        },
      },
    },
    scales: {
      r: {
        beginAtZero: true,
        max: 100,
        min: 0,
        ticks: {
          stepSize: 25,
          display: true,
          color: "rgba(26, 36, 48, 0.58)",
          font: {
            size: 12,
            weight: "600",
          },
          backdropColor: "rgba(255, 255, 255, 0.9)",
          backdropPadding: 4,
          callback: function (value) {
            if (value === 0) return "0%";
            if (value === 50) return "50%";
            if (value === 100) return "100%";
            return "";
          },
        },
        grid: {
          color: [
            "rgba(15, 119, 213, 0.22)",
            "rgba(15, 119, 213, 0.16)",
            "rgba(15, 119, 213, 0.1)",
          ],
          lineWidth: 1,
          circular: true,
        },
        pointLabels: {
          color: "#1a2430",
          font: {
            size: 11,
            weight: "600",
            family: "Sora, sans-serif",
          },
          padding: 15,
          backdropColor: "rgba(244, 247, 251, 0.94)",
          backdropPadding: {
            x: 8,
            y: 4,
          },
          clip: false,
          display: true,
          callback: function (label) {
            // Split label into words
            const words = label.split(" ");

            // If 2 words or less, return as single line
            if (words.length <= 2) {
              return label;
            }

            // Break after every 2 words and return as array
            const lines = [];
            for (let i = 0; i < words.length; i += 2) {
              lines.push(words.slice(i, i + 2).join(" "));
            }

            return lines;
          },
        },
        angleLines: {
          color: "rgba(15, 119, 213, 0.16)",
          lineWidth: 1,
        },
      },
    },
  };

  // Mapping from card categories to API section names
  const sectionMapping = {
    "project-type": "Business Strategy & Vision Alignment",
    maturity: "Business readiness",
    sector: "AI Readiness & Adoption Strategy",
    function: "Technology Readiness",
    capability: "Data Readiness",
    impact: "Risk Management & Governance",
    dummy1: "Talent readiness",
    dummy2: "Impact, Value & Performance Measurement",
    dummy3: "Cost effectiveness",
  };

  const toggleCard = (cardIndex) => {
    setOpenCard(openCard === cardIndex ? null : cardIndex);
  };

  const getAnswerSummary = (category) => {
    return cardAnswers[category] || "Not provided";
  };

  const getAnswerClassName = (category) => {
    return getAnswerSummary(category)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-");
  };

  // Get assessment data for a specific category
  const getAssessmentData = (category) => {
    const sectionName = sectionMapping[category];
    if (!sectionName || !assessmentReport[sectionName]) {
      return { analysis: "", next_steps: "" };
    }
    return {
      analysis: assessmentReport[sectionName].analysis || "",
      next_steps: assessmentReport[sectionName].next_steps || "",
    };
  };

  return (
    <div className="report--steel">
      <style>{`
      body, html {
        background: #ffffff !important;
        background-attachment: fixed !important;
        min-height: 100vh;
      }

      .page-background::before {
        content: "";
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        min-height: 100vh;
        background: #ffffff;
        z-index: -2;
        pointer-events: none;
      }

      .roadmap-page {
        background: transparent !important;
        min-height: 100vh;
      }

      .hero-section {
        background: transparent !important;
      }

      .footer {
        background: transparent !important;
      }
    `}</style>
      <div className="page-background">
        <svg
          className="hero-triangle-bg"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient
              id="lineGradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stopColor="#5B9BD5" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#FD6061" stopOpacity="0.2" />
            </linearGradient>
          </defs>
          <g stroke="url(#lineGradient)" fill="none" strokeWidth="0.1">
            <line x1="10" y1="10" x2="90" y2="90" className="animated-line" />
            <line x1="20" y1="5" x2="95" y2="80" className="animated-line" />
            <line x1="5" y1="20" x2="80" y2="95" className="animated-line" />
            <line x1="30" y1="10" x2="90" y2="70" className="animated-line" />
            <line x1="10" y1="30" x2="70" y2="90" className="animated-line" />
          </g>
        </svg>
      </div>
      <div className="roadmap-page">
        <Navbar />

        {/* Hero Section */}
        <section className="hero-section" id="reimagine-light">
          <div className="hero-content">
            <div className="row justify-content-center mx-0">
              <div className="col-12 text-center hero-text">
                <h1
                  className="hero__headline"
                  style={{ marginTop: "80px", color: "#ffffff" }}
                >
                  <span style={{ fontWeight: 300, color: "#cacaca" }}>Re</span>
                  <span style={{ fontWeight: 600 }}>Imagine</span>
                  <span
                    style={{
                      fontWeight: 600,
                      marginLeft: "8px",
                      background:
                        "linear-gradient(135deg, rgb(253, 96, 97), rgb(91, 155, 213))",
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      color: "transparent",
                    }}
                  >
                    Lite
                  </span>
                </h1>
                <p
                  style={{
                    marginTop: "15px",
                    fontSize: "1.25rem",
                    color: "#cacaca",
                  }}
                >
                  Your AI roadmap is drawn from your responses to the key
                  planning areas
                </p>
              </div>
            </div>
          </div>

          {/* Filter Cards Section */}
          <div className="filters-section container">
            <div className="filters-grid" id="filters-accordion">
              {/* Project Type */}
              <div
                className={`filter-card ${openCard === "project-type" ? "expanded" : ""}`}
              >
                <div className="filter-card__header gradient--indigo">
                  <span className="filter-card__title">
                    Business Strategy & Vision Alignment
                  </span>
                </div>
                <div className="filter-summary locked-summary">
                  <div
                    className={`response-badge ${getAnswerClassName("project-type")}`}
                  >
                    {getAnswerSummary("project-type")}
                  </div>
                </div>
              </div>

              {/* Current data+AI Maturity Level */}
              <div
                className={`filter-card ${openCard === "maturity" ? "expanded" : ""}`}
              >
                <div className="filter-card__header gradient--amber">
                  <span className="filter-card__title">Business Readiness</span>
                </div>
                <div className="filter-summary locked-summary">
                  <div
                    className={`response-badge ${getAnswerClassName("maturity")}`}
                  >
                    {getAnswerSummary("maturity")}
                  </div>
                </div>
              </div>

              {/* Business Sector */}
              <div
                className={`filter-card ${openCard === "sector" ? "expanded" : ""}`}
              >
                <div className="filter-card__header gradient--orange">
                  <span className="filter-card__title">
                    AI Readiness & Adoption Strategy
                  </span>
                </div>
                <div className="filter-summary locked-summary">
                  <div
                    className={`response-badge ${getAnswerClassName("sector")}`}
                  >
                    {getAnswerSummary("sector")}
                  </div>
                </div>
              </div>

              {/* Business Function */}
              <div
                className={`filter-card ${openCard === "function" ? "expanded" : ""}`}
              >
                <div className="filter-card__header gradient--teal">
                  <span className="filter-card__title">
                    Technology Readiness
                  </span>
                </div>
                <div className="filter-summary locked-summary">
                  <div
                    className={`response-badge ${getAnswerClassName("function")}`}
                  >
                    {getAnswerSummary("function")}
                  </div>
                </div>
              </div>

              {/* Business Capability Focus */}
              <div
                className={`filter-card ${openCard === "capability" ? "expanded" : ""}`}
              >
                <div className="filter-card__header gradient--cyan">
                  <span className="filter-card__title">Data Readiness</span>
                </div>
                <div className="filter-summary locked-summary">
                  <div
                    className={`response-badge ${getAnswerClassName("capability")}`}
                  >
                    {getAnswerSummary("capability")}
                  </div>
                </div>
              </div>

              {/* Impact, Value & Performance Measurement */}
              <div
                className={`filter-card ${openCard === "impact" ? "expanded" : ""}`}
              >
                <div className="filter-card__header gradient--violet">
                  <span className="filter-card__title">
                    Risk Management & Governance
                  </span>
                </div>
                <div className="filter-summary locked-summary">
                  <div
                    className={`response-badge ${getAnswerClassName("impact")}`}
                  >
                    {getAnswerSummary("impact")}
                  </div>
                </div>
              </div>

              {/* Dummy Card 1 */}
              <div
                className={`filter-card ${openCard === "dummy1" ? "expanded" : ""}`}
              >
                <div className="filter-card__header gradient--indigo">
                  <span className="filter-card__title">Talent Readiness</span>
                </div>
                <div className="filter-summary locked-summary">
                  <div
                    className={`response-badge ${getAnswerClassName("dummy1")}`}
                  >
                    {getAnswerSummary("dummy1")}
                  </div>
                </div>
              </div>

              {/* Dummy Card 2 */}
              <div
                className={`filter-card ${openCard === "dummy2" ? "expanded" : ""}`}
              >
                <div className="filter-card__header gradient--amber">
                  <span className="filter-card__title">
                    Impact, Value & Performance Measurement
                  </span>
                </div>
                <div className="filter-summary locked-summary">
                  <div
                    className={`response-badge ${getAnswerClassName("dummy2")}`}
                  >
                    {getAnswerSummary("dummy2")}
                  </div>
                </div>
              </div>

              {/* Dummy Card 3 */}
              <div
                className={`filter-card ${openCard === "dummy3" ? "expanded" : ""}`}
              >
                <div className="filter-card__header gradient--orange">
                  <span className="filter-card__title">Cost Effectiveness</span>
                </div>
                <div className="filter-summary locked-summary">
                  <div
                    className={`response-badge ${getAnswerClassName("dummy3")}`}
                  >
                    {getAnswerSummary("dummy3")}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* AI Assessment Report Section */}
          <section className="ai-assessment-report">
            <div className="container">
              <div className="report-header">
                <h2 className="report-title">
                  <span className="report-title-gradient">Assessment</span> Report
                </h2>
                <p className="report-subtitle">
                  Comprehensive analysis of your organization's AI capabilities
                </p>
              </div>

              {/* Report Overview */}
              <div className="report-overview">
                <div className="overview-container">
                  {/* AI Readiness Chart */}
                  <div className="chart-section">
                    <div className="chart-header">
                      <h3>Assessment <span className="gradient-text" style={{fontWeight: 700}}>Readiness</span></h3>
                      <p>Performance Across Key Areas</p>
                    </div>

                    {/* Spider Chart */}
                    {spiderChartData ? (
                      <div className="spider-chart-wrapper">
                        <div className="spider-chart-container">
                          <div className="spider-chart-inner">
                            <Radar
                              data={spiderChartData}
                              options={spiderChartOptions}
                            />
                          </div>
                          <div className="spider-chart-glow"></div>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="spider-chart-container"
                        style={{
                          padding: "2rem",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          minHeight: "400px",
                        }}
                      >
                        <p style={{ color: "#b8c5d1" }}>
                          No score data available
                        </p>
                      </div>
                    )}

                    <div className="chart-legend">
                      <div className="legend-item">
                        <div className="legend-color green"></div>
                        <span>Strong (100%)</span>
                      </div>
                      <div className="legend-item">
                        <div className="legend-color yellow"></div>
                        <span>Moderate (50%)</span>
                      </div>
                      <div className="legend-item">
                        <div className="legend-color red"></div>
                        <span>Needs Work (0%)</span>
                      </div>
                    </div>
                  </div>

                  {/* Overview Text */}
                  <div className="overview-card">
                    <h3>Assessment <span className="gradient-text-bold">Overview</span></h3>
                    <p>
                      This AI Readiness Assessment evaluates the organization's
                      preparedness across key dimensions essential for
                      successful AI adoption and integration. The assessment
                      covers Business Strategy & Vision Alignment, Business
                      Readiness, AI Readiness & Adoption Strategy, Technology
                      Readiness, Data Readiness, Risk Management & Governance,
                      Talent Readiness, Impact Measurement, and Cost
                      Effectiveness.
                    </p>

                    <div className="key-metrics">
                      <div className="metric-item">
                        <div className="metric-value">{strongCount}</div>
                        <div className="metric-label">Strong Areas</div>
                      </div>
                      <div className="metric-item">
                        <div className="metric-value">{moderateCount}</div>
                        <div className="metric-label">Moderate Areas</div>
                      </div>
                      <div className="metric-item">
                        <div className="metric-value">{needsFocusCount}</div>
                        <div className="metric-label">
                          Needs Focus
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Assessment Results Grid */}
              <div className="assessment-results">
                <div className="result-card">
                  <div className="result-header">
                    <h4>1. Business Strategy & Vision Alignment</h4>
                    <div
                      className={`response-badge ${getAnswerClassName("project-type")}`}
                    >
                      {getAnswerSummary("project-type")}
                    </div>
                  </div>
                  <div className="result-content">
                    <div className="analysis">
                      <h5>Analysis:</h5>
                      <p>
                        {getAssessmentData("project-type").analysis ||
                          "Your AI strategy is well-aligned with your overall business goals. It is clearly defined, communicated, and integrated into your organization's objectives. This is a strong foundation, and you are on the right path to leveraging AI for tangible business outcomes."}
                      </p>
                    </div>
                    <div className="next-steps">
                      <h5>Next Steps:</h5>
                      <p>
                        {getAssessmentData("project-type").next_steps ||
                          "Continue ensuring that AI initiatives remain tightly coupled with your organization's evolving business priorities. Keep tracking the alignment and make necessary adjustments as goals shift."}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="result-card">
                  <div className="result-header">
                    <h4>2. Business Readiness</h4>
                    <div
                      className={`response-badge ${getAnswerClassName("maturity")}`}
                    >
                      {getAnswerSummary("maturity")}
                    </div>
                  </div>
                  <div className="result-content">
                    <div className="analysis">
                      <h5>Analysis:</h5>
                      <p>
                        {getAssessmentData("maturity").analysis ||
                          "Your organization shows moderate commitment to AI initiatives. While leadership support exists, there may be gaps in fully understanding the organizational changes and investments required for successful AI transformation."}
                      </p>
                    </div>
                    <div className="next-steps">
                      <h5>Next Steps:</h5>
                      <p>
                        {getAssessmentData("maturity").next_steps ||
                          "Engage stakeholders across all levels to build stronger commitment. Ensure clear communication about resource requirements, timelines, and expected organizational changes needed for AI success."}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="result-card">
                  <div className="result-header">
                    <h4>3. AI Readiness & Adoption Strategy</h4>
                    <div
                      className={`response-badge ${getAnswerClassName("sector")}`}
                    >
                      {getAnswerSummary("sector")}
                    </div>
                  </div>
                  <div className="result-content">
                    <div className="analysis">
                      <h5>Analysis:</h5>
                      <p>
                        {getAssessmentData("sector").analysis ||
                          "Your organization may not have yet identified or prioritized AI use cases that align with measurable business outcomes. This is an essential step to ensure AI investments are focused on areas that will drive real business value."}
                      </p>
                    </div>
                    <div className="next-steps">
                      <h5>Next Steps:</h5>
                      <p>
                        {getAssessmentData("sector").next_steps ||
                          "Start by engaging key stakeholders to identify high-impact use cases. Prioritize them based on their potential to drive business outcomes such as increased revenue, cost savings, or operational efficiency."}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="result-card">
                  <div className="result-header">
                    <h4>4. Technology Readiness</h4>
                    <div
                      className={`response-badge ${getAnswerClassName("function")}`}
                    >
                      {getAnswerSummary("function")}
                    </div>
                  </div>
                  <div className="result-content">
                    <div className="analysis">
                      <h5>Analysis:</h5>
                      <p>
                        {getAssessmentData("function").analysis ||
                          "Your current technology infrastructure may partially support AI initiatives but might lack full modularity or flexibility to integrate various AI models seamlessly. It's important that your system architecture can adapt as new models are introduced and scaled."}
                      </p>
                    </div>
                    <div className="next-steps">
                      <h5>Next Steps:</h5>
                      <p>
                        {getAssessmentData("function").next_steps ||
                          "Evaluate your existing technical foundation and explore opportunities to enhance modularity. Build a scalable AI architecture that supports future needs, integrating multiple GenAI models and applications easily."}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="result-card">
                  <div className="result-header">
                    <h4>5. Data Readiness</h4>
                    <div
                      className={`response-badge ${getAnswerClassName("capability")}`}
                    >
                      {getAnswerSummary("capability")}
                    </div>
                  </div>
                  <div className="result-content">
                    <div className="analysis">
                      <h5>Analysis:</h5>
                      <p>
                        {getAssessmentData("capability").analysis ||
                          "Your organization has access to quality data from internal, external, or third-party sources. This strong data foundation is crucial for building effective AI solutions and driving meaningful insights."}
                      </p>
                    </div>
                    <div className="next-steps">
                      <h5>Next Steps:</h5>
                      <p>
                        {getAssessmentData("capability").next_steps ||
                          "Continue to maintain data quality standards and explore opportunities to enrich your data ecosystem. Ensure proper data governance and accessibility for AI use cases."}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="result-card">
                  <div className="result-header">
                    <h4>6. Risk Management & Governance</h4>
                    <div
                      className={`response-badge ${getAnswerClassName("impact")}`}
                    >
                      {getAnswerSummary("impact")}
                    </div>
                  </div>
                  <div className="result-content">
                    <div className="analysis">
                      <h5>Analysis:</h5>
                      <p>
                        {getAssessmentData("impact").analysis ||
                          "It seems your organization is in the early stages of establishing governance frameworks for AI. While there might be some frameworks in place, there may still be gaps in ensuring the responsible, ethical, and compliant use of AI, especially regarding privacy, security, and ethics."}
                      </p>
                    </div>
                    <div className="next-steps">
                      <h5>Next Steps:</h5>
                      <p>
                        {getAssessmentData("impact").next_steps ||
                          "Consider implementing comprehensive governance policies across AI applications, ensuring full coverage on security, privacy, and compliance. Conduct regular audits to assess and update these frameworks to stay aligned with industry standards."}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="result-card">
                  <div className="result-header">
                    <h4>7. Talent Readiness</h4>
                    <div
                      className={`response-badge ${getAnswerClassName("dummy1")}`}
                    >
                      {getAnswerSummary("dummy1")}
                    </div>
                  </div>
                  <div className="result-content">
                    <div className="analysis">
                      <h5>Analysis:</h5>
                      <p>
                        {getAssessmentData("dummy1").analysis ||
                          "Your teams demonstrate strong domain expertise and technical skills necessary for AI adoption. This combination of business knowledge and technical capability positions your organization well for successful AI implementation."}
                      </p>
                    </div>
                    <div className="next-steps">
                      <h5>Next Steps:</h5>
                      <p>
                        {getAssessmentData("dummy1").next_steps ||
                          "Develop a structured training and upskilling program to keep teams updated with emerging AI technologies. Foster a culture of continuous learning to maintain your competitive advantage."}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="result-card">
                  <div className="result-header">
                    <h4>8. Impact, Value & Performance Measurement</h4>
                    <div
                      className={`response-badge ${getAnswerClassName("dummy2")}`}
                    >
                      {getAnswerSummary("dummy2")}
                    </div>
                  </div>
                  <div className="result-content">
                    <div className="analysis">
                      <h5>Analysis:</h5>
                      <p>
                        {getAssessmentData("dummy2").analysis ||
                          "While some metrics are in place, there may be gaps in comprehensively tracking AI impact, ROI, and performance. Establishing clear measurement frameworks is essential for demonstrating value and guiding future investments."}
                      </p>
                    </div>
                    <div className="next-steps">
                      <h5>Next Steps:</h5>
                      <p>
                        {getAssessmentData("dummy2").next_steps ||
                          "Define clear KPIs and metrics aligned with business objectives. Implement regular monitoring and reporting mechanisms to track AI initiative performance and demonstrate tangible business value."}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="result-card">
                  <div className="result-header">
                    <h4>9. Cost Effectiveness</h4>
                    <div
                      className={`response-badge ${getAnswerClassName("dummy3")}`}
                    >
                      {getAnswerSummary("dummy3")}
                    </div>
                  </div>
                  <div className="result-content">
                    <div className="analysis">
                      <h5>Analysis:</h5>
                      <p>
                        {getAssessmentData("dummy3").analysis ||
                          "Your organization has secured adequate funding to initiate and scale AI transformation initiatives. This financial commitment demonstrates strong leadership support and enables sustainable AI development."}
                      </p>
                    </div>
                    <div className="next-steps">
                      <h5>Next Steps:</h5>
                      <p>
                        {getAssessmentData("dummy3").next_steps ||
                          "Ensure efficient allocation of resources across AI initiatives. Establish clear ROI tracking mechanisms to justify continued investment and optimize spending based on performance and business impact."}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Final Thoughts and Recommendations */}
              <div className="recommendations-sections">
                <div className="recommendations-card">
                  <h3>Final <span className="gradient-text" style={{fontWeight: 700}}>Thoughts</span> and <span className="gradient-text" style={{fontWeight: 700}}>Recommendations</span></h3>
                  <p>
                    {summary ||
                      "Your organization is on the right track with AI strategy and operational implementation. However, there are critical gaps in governance, use case prioritization, and people development. By addressing these areas, you can accelerate AI adoption and realize greater business value."}
                  </p>

                  <div className="next-steps-grid">
                    {actionPlan.length > 0 ? (
                      actionPlan.map((item, index) => {
                        // Extract key and value from the action plan item
                        const itemKey = Object.keys(item)[0];
                        const itemValue = item[itemKey];
                        return (
                          <div key={index} className="step-item">
                            <div className="step-number">{index + 1}</div>
                            <div className="step-content">
                              <h4>{itemKey}</h4>
                              <p>{itemValue}</p>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <>
                        <div className="step-item">
                          <div className="step-number">1</div>
                          <div className="step-content">
                            <h4>Set up governance framework</h4>
                            <p>
                              Clear policies for data, models, and ethical
                              considerations
                            </p>
                          </div>
                        </div>
                        <div className="step-item">
                          <div className="step-number">2</div>
                          <div className="step-content">
                            <h4>Identify key use cases</h4>
                            <p>
                              AI applications that can provide measurable ROI
                            </p>
                          </div>
                        </div>
                        <div className="step-item">
                          <div className="step-number">3</div>
                          <div className="step-content">
                            <h4>Invest in AI architecture</h4>
                            <p>
                              Design patterns that support future scalability
                            </p>
                          </div>
                        </div>
                        <div className="step-item">
                          <div className="step-number">4</div>
                          <div className="step-content">
                            <h4>Upskill teams</h4>
                            <p>
                              Continuous AI education programs for all levels
                            </p>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Proceed to Signin Button */}
              <div
                className="generate-roadmap-section"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <p
                  style={{
                    color: "#cacaca",
                    textAlign: "center",
                    marginBottom: "20px",
                    fontSize: "22px",
                    fontWeight: "300",
                  }}
                >
                  Want a detailed assessment of{" "}
                  <span className="title-word--primary" style={{ fontWeight: 700 }}>
                    data+AI
                  </span>{" "}
                  strategy with <span style={{ fontWeight: 300 }}>Re</span>
                  <span style={{ fontWeight: 500 }}>Imagine</span> <span style={{ fontWeight: 300, fontStyle: "italic" }}>
                    2.0
                  </span>
                  ?
                </p>
                <div
                  style={{
                    display: "flex",
                    gap: "20px",
                    justifyContent: "center",
                    flexWrap: "wrap",
                    marginTop: "20px",
                  }}
                >
                  <button
                    className="hero__cta_2 generate-roadmap-btn"
                    onClick={handleProceedToSignin}
                    style={{
                      "--spacing-sm": "min(1vw, 1.5vh)",
                      "--spacing-lg": "min(2vw, 3vh)",
                      padding: "var(--spacing-sm) var(--spacing-lg)",
                    }}
                  >
                    <span className="hero__cta_2-dot"></span>
                    Click to explore
                  </button>
                  <button
                    className="hero__cta_2 generate-roadmap-btn"
                    onClick={handleGenerateNewReport}
                    style={{
                      "--spacing-sm": "min(1vw, 1.5vh)",
                      "--spacing-lg": "min(2vw, 3vh)",
                      padding: "var(--spacing-sm) var(--spacing-lg)",
                    }}
                  >
                    <span className="hero__cta_2-icon">
                    </span>
                    Generate New Report
                  </button>
                </div>
              </div>
            </div>
          </section>
        </section>

        <ScrollToExplore />
        <Footer />
      </div>
    </div>
  );
}

export default Roadmap;
